import { compact, get, uniq } from 'lodash';

import { Locale } from '@/i18n/locales';
import { ClientSafeConfig, PartialTenantConfig, TenantConfig } from '@/lib/types/tenant';

const ASSISTANT_NAME_KEY = 'chat.assistantName';
const ASSISTANT_NAME_PLACEHOLDER = /\{assistantName\}/g;

export function getLocalizedValue<T>(
  config: TenantConfig | ClientSafeConfig,
  currentLocale: Locale,
  keyPath: string,
): T | null {
  const { meta, translations } = config;
  const { language: defaultLocale, availableLocales = [defaultLocale] } = meta;

  const fallbackChain = uniq(compact([currentLocale, defaultLocale, ...availableLocales]));

  for (const locale of fallbackChain) {
    const value = get(translations[locale], keyPath);
    if (value !== undefined) {
      return interpolateAssistantName({ value, config, currentLocale, keyPath }) as T;
    }
  }

  return null;
}

/**
 * Substitutes `{assistantName}` in any localized string with the tenant's
 * `chat.assistantName`, also resolved through the same locale fallback chain.
 * Lets every tenant text field (meta.title, sidebarTitle, tooltipText,
 * welcome*, etc.) reference the brand once via the placeholder instead of
 * repeating the literal string per locale.
 */
function interpolateAssistantName({
  value,
  config,
  currentLocale,
  keyPath,
}: {
  value: unknown;
  config: TenantConfig | ClientSafeConfig;
  currentLocale: Locale;
  keyPath: string;
}): unknown {
  if (typeof value !== 'string') return value;
  if (!value.includes('{assistantName}')) return value;
  // Recursion guard: resolving `chat.assistantName` itself must not loop back.
  if (keyPath === ASSISTANT_NAME_KEY) return value;
  const assistantName = getLocalizedValue<string>(config, currentLocale, ASSISTANT_NAME_KEY);
  if (!assistantName) return value;
  return value.replace(ASSISTANT_NAME_PLACEHOLDER, assistantName);
}

export function getLocalesFromConfig(config: PartialTenantConfig): string[] {
  const { translations } = config;
  if (!translations) return [];
  return Object.keys(translations).filter(key => translations[key as keyof typeof translations]);
}
